// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@rpsc/MobiusOverrides/Button/Button";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import IconBadgePlaceholder from "@rpsc/XDL/Icons/IconBadgePlaceholder";
import React, { FC } from "react";

interface AccountOverviewStyles {
    wrapper?: InjectableCss;
    tileList?: InjectableCss;
    tile?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
}

export const AccountOverviewStyles: AccountOverviewStyles = {};

const styles = AccountOverviewStyles;

const enum fields {
    tileList = "tileList",
}

interface AccountTilesListProps {
    fields: {
        title: string;
        image: string;
        link: LinkFieldValue;
        linkText: string;
        variant: "primary" | "secondary" | "tertiary" | "link";
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.tileList]: AccountTilesListProps[];
    };
}
type Props = OwnProps;

const AccountOverview: FC<Props> = ({ fields: { tileList } }) => {
    const history = useHistory();
    const links = useGetLinks(tileList, l => l.fields.link);
    const isInShell = useShellContext().isInShell;

    const goTo = (url?: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.tileList}>
                {tileList.map((item, index) => {
                    const buttonLink = links[index];
                    return (
                        <StyledWrapper key={index} {...styles.tile}>
                            <StyledWrapper {...styles.titleWrapper}>
                                {item.fields.image ? (
                                    <LazyImage
                                        {...styles.image}
                                        src={item.fields.image}
                                        altText={`${item.fields.title} image`}
                                    />
                                ) : (
                                    <Icon src={IconBadgePlaceholder} width={34} height={34} />
                                )}
                                <Typography variant="h4" forwardAs="h2" {...styles.title}>
                                    {item.fields.title}
                                </Typography>
                            </StyledWrapper>
                            {!!buttonLink?.url &&
                                (item.fields.variant === "link" ? (
                                    <Link
                                        href={buttonLink.url}
                                        aria-label={item.fields.linkText || buttonLink.title}
                                        {...styles.link}
                                    >
                                        {item.fields.linkText || buttonLink.title}
                                    </Link>
                                ) : (
                                    <Button sizeVariant="small" variant={item.fields.variant as any} onClick={() => goTo(buttonLink.url)}>
                                        {item.fields.linkText || buttonLink.title}
                                    </Button>
                                ))}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const AccountOverviewWidget: WidgetModule = {
    component: AccountOverview,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.tileList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                    },
                    {
                        name: "linkText",
                        displayName: "Action Label",
                        editorTemplate: "TextField",
                        defaultValue: "Discover",
                    },
                    {
                        name: "variant",
                        displayName: "Action Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                            // xng-change
                            { displayName: "Link", value: "link" },
                        ],
                        defaultValue: "primary",
                    },
                ],
            },
        ],
    },
};
export default AccountOverviewWidget;
